import { DateTime } from 'luxon';
import { useCallback, useEffect, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';
import {
  backgroundColor,
  borderColor,
  borderRadius,
  borderWidth,
  display,
  fontSize,
  fontWeight,
  height,
  inset,
  justifyContent,
  margin,
  padding,
  position,
  tailwindClassNameConvertNegative,
  textColor,
  TRotate,
  twCls,
  width,
} from 'style';

interface EnergyBarData {
  arg: number;
  argName: string;
  date: DateTime;
  value: number;
}

interface EnergyLineData {
  average: number;
}

export enum EnergyDisplayMode {
  Weekly = 'weekly',
  Monthly = 'monthly',
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CustomTooltip = ({ active, payload, type }: any) => {
  if (active) {
    return (
      <div
        className={twCls(
          backgroundColor('bg-white'),
          borderWidth('border'),
          borderColor('border-grey-700'),
          borderRadius('rounded-lg'),
          padding('px-2', 'py-1'),
        )}
      >
        <p className={twCls(fontSize('text-sm'))}>
          {type === EnergyDisplayMode.Weekly
            ? payload[0]?.payload?.date?.toFormat('ccc, LLL dd')
            : payload[0]?.payload?.date?.toFormat('LLL y')}
        </p>
        <p className={twCls(textColor('text-blue-100'))}>
          {payload[0]?.value}
          {payload[0]?.unit}
        </p>
      </div>
    );
  }

  return null;
};

export interface EnergyUseChartProps {
  title: string;
  type: EnergyDisplayMode;
  data: number[];
  average: number;
}

export const EnergyUseChart = ({
  title,
  type,
  data,
  average,
}: EnergyUseChartProps): JSX.Element => {
  const [energyBarData, setEnergyBarData] = useState<EnergyBarData[]>([]);
  const [energyLineData, setEnergyLineData] = useState<EnergyLineData[]>([]);

  const getArgName = useCallback(
    (currentDate: DateTime, index: number) => {
      switch (type) {
        case EnergyDisplayMode.Weekly:
          return currentDate.minus({ days: index }).toFormat('ccccc');
        case EnergyDisplayMode.Monthly:
          return currentDate.minus({ months: index }).toFormat('LLLLL');
        default:
          throw new Error('Invalid type');
      }
    },
    [type],
  );

  const getDate = useCallback(
    (currentDate: DateTime, index: number) => {
      switch (type) {
        case EnergyDisplayMode.Weekly:
          return currentDate.minus({ days: index });
        case EnergyDisplayMode.Monthly:
          return currentDate.minus({ months: index });
        default:
          throw new Error('Invalid type');
      }
    },
    [type],
  );

  const convertToChartData = useCallback(
    (values: number[], currentDate: DateTime) => {
      const chartArray: EnergyBarData[] = [];
      values.forEach((v, i) =>
        chartArray.push({
          arg: i,
          argName: getArgName(currentDate, i),
          date: getDate(currentDate, i),
          value: Number((v / 1000).toFixed(2)),
        }),
      );
      return chartArray;
    },
    [getArgName, getDate],
  );

  useEffect(() => {
    const currentDate = DateTime.now().startOf('day');

    const energyBarDataArray: EnergyBarData[] = convertToChartData(data, currentDate);
    setEnergyBarData(energyBarDataArray.reverse());

    // filling 100 elements is a workaround to have line on full grid width. Otherwise it's spanned from first to last bar.
    const energyLineDataArray: EnergyLineData[] = new Array(100).fill({
      average: Number((average / 1000).toFixed(2)),
    });
    setEnergyLineData(energyLineDataArray);
  }, [average, convertToChartData, data]);

  const averageAvailable =
    energyLineData && energyLineData.length > 0 && energyLineData[0].average !== 0;

  return (
    <>
      <div className={twCls(fontWeight('font-bold'))}>{title}</div>
      {averageAvailable && (
        <div
          className={twCls(
            display('flex'),
            justifyContent('justify-end'),
            fontSize('text-xs'),
            margin('mr-2'),
          )}
        >
          <hr
            className={twCls(
              width('w-4'),
              height('h-0.5'),
              backgroundColor('bg-blue-100'),
              margin('mt-1.5', 'mr-1'),
            )}
          />
          Average Use
        </div>
      )}
      <div className={twCls(position('relative'))}>
        <ResponsiveContainer height={200}>
          <ComposedChart
            data={energyBarData}
            margin={{
              top: 8,
              right: 10,
              left: type === EnergyDisplayMode.Weekly ? -12 : -2,
              bottom: 10,
            }}
          >
            <CartesianGrid vertical={false} />
            <XAxis
              xAxisId="barDataX"
              dataKey="argName"
              tickLine={false}
              tickMargin={10}
              stroke="#b2b2b2"
              tick={{ fill: '#777777', fontSize: 12 }}
            />
            <YAxis
              yAxisId="dataY"
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#777777', fontSize: 12 }}
            />
            <XAxis xAxisId="lineDataX" hide={true} />
            <Tooltip content={<CustomTooltip type={type} />} />
            <Bar
              xAxisId="barDataX"
              yAxisId="dataY"
              data={energyBarData}
              dataKey="value"
              fill="#118dff"
              unit={' kWh'}
              radius={[100, 100, 100, 100]}
              barSize={7}
            />
            {averageAvailable && (
              <Line
                xAxisId="lineDataX"
                yAxisId="dataY"
                data={energyLineData}
                dataKey="average"
                strokeWidth={1.5}
                stroke="#118dff"
                dot={false}
                activeDot={false}
                unit={' kWh'}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
        <div
          className={twCls(
            position('absolute'),
            inset('top-0', 'left-0'),
            margin('mt-2.5'),
            fontSize('text-sm'),
            width('w-min'),
            tailwindClassNameConvertNegative<TRotate>('-rotate-90'),
          )}
        >
          kWh
        </div>
      </div>
    </>
  );
};
